import { useEffect, useMemo, useState } from 'react'
import { useRepos } from '../../state/repos'
import type { ListItem } from '../../db/repositories'
import type { BookContent, GarmentContent, GarmentSection } from '../../types/records'
import { GarmentDetail } from './GarmentDetail'
import {
  diagnoseAll,
  type DiagnosticSummary,
  type GarmentDiagnostic,
} from '../../geometry/diagnoseGarments'
import { isBaseGarment } from '../../geometry/baseResolver'

const CATEGORY_LABELS: Record<string, string> = {
  bodice: 'Corpetto',
  shirt: 'Camicia',
  blouse: 'Camicetta',
  dress: 'Abito',
  skirt: 'Gonna',
  trousers: 'Pantalone',
  jacket: 'Giacca',
  coat: 'Cappotto',
  vest: 'Gilet',
  sleeve: 'Manica',
  collar: 'Colletto',
  other: 'Altro',
}

const SECTION_LABELS: Record<GarmentSection, string> = {
  donna: 'Donna',
  uomo: 'Uomo',
  bambino: 'Bambino',
}

interface Props {
  onCreateProjectFromGarment?: (garmentId: string) => void
}

export function GarmentsTab({ onCreateProjectFromGarment }: Props) {
  const { garments, books } = useRepos()
  const [items, setItems] = useState<ListItem<GarmentContent>[]>([])
  const [book, setBook] = useState<BookContent | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [filterSection, setFilterSection] = useState<GarmentSection | 'all'>('all')
  const [filterCategory, setFilterCategory] = useState<string>('all')
  const [search, setSearch] = useState('')
  const [diagnostic, setDiagnostic] = useState<DiagnosticSummary | null>(null)
  const [diagBusy, setDiagBusy] = useState(false)
  const [filterStatus, setFilterStatus] = useState<'all' | 'ok' | 'warn' | 'error'>('all')

  useEffect(() => {
    void (async () => {
      const [bookList, garmentList] = await Promise.all([books.list(), garments.list()])
      setBook(bookList[0]?.content ?? null)
      setItems(garmentList)
      setLoaded(true)
    })()
  }, [garments, books])

  const categories = useMemo(() => {
    const set = new Set<string>()
    items.forEach(i => set.add(i.content.category))
    return [...set].sort()
  }, [items])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return items.filter(i => {
      if (filterSection !== 'all' && i.content.section !== filterSection) return false
      if (filterCategory !== 'all' && i.content.category !== filterCategory) return false
      if (q && !i.content.name.toLowerCase().includes(q)) return false
      return true
    })
  }, [items, filterSection, filterCategory, search])

  const diagByName = useMemo(() => {
    const m = new Map<string, GarmentDiagnostic>()
    if (diagnostic) for (const d of diagnostic.results) m.set(d.garmentName, d)
    return m
  }, [diagnostic])

  // Ordinamento "consigliato": basi prima, poi per stato (ok > warn > error),
  // infine alfabetico. Senza diagnostica → solo alfabetico.
  const STATUS_RANK: Record<string, number> = { ok: 0, warn: 1, error: 2 }
  const sortedAlpha = useMemo(() => {
    const arr = [...filtered]
    arr.sort((a, b) => {
      if (diagnostic) {
        const ba = isBaseGarment(a.content.name) ? 0 : 1
        const bb = isBaseGarment(b.content.name) ? 0 : 1
        if (ba !== bb) return ba - bb
        const sa = STATUS_RANK[diagByName.get(a.content.name)?.status ?? ''] ?? 3
        const sb = STATUS_RANK[diagByName.get(b.content.name)?.status ?? ''] ?? 3
        if (sa !== sb) return sa - sb
      }
      return a.content.name.localeCompare(b.content.name, 'it')
    })
    return arr
  }, [filtered, diagnostic, diagByName])

  const visible = useMemo(() => {
    if (!diagnostic || filterStatus === 'all') return sortedAlpha
    return sortedAlpha.filter(item => {
      const d = diagByName.get(item.content.name)
      return d?.status === filterStatus
    })
  }, [sortedAlpha, diagnostic, filterStatus, diagByName])

  async function runDiagnostic() {
    if (!book) return
    setDiagBusy(true)
    // setTimeout per dare al browser il tempo di renderizzare lo stato "busy"
    await new Promise(r => setTimeout(r, 30))
    try {
      const summary = diagnoseAll(items.map(i => i.content), book)
      setDiagnostic(summary)
    } finally {
      setDiagBusy(false)
    }
  }

  // Auto-diagnostica una volta, appena dati pronti (pill sempre visibili)
  useEffect(() => {
    if (loaded && book && items.length > 0 && !diagnostic && !diagBusy) {
      void runDiagnostic()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, book, items])

  if (!loaded) return <p className="muted">Caricamento…</p>

  if (selectedId) {
    const found = items.find(i => i.id === selectedId)
    if (found) {
      return (
        <GarmentDetail
          garmentId={found.id}
          garment={found.content}
          book={book}
          onBack={() => setSelectedId(null)}
          onCreateProject={onCreateProjectFromGarment}
        />
      )
    }
  }

  if (items.length === 0) {
    return (
      <section className="tab-section">
        <h3>Capi</h3>
        <div className="empty-state">
          <p>Nessun capo configurato.</p>
          <p className="hint">
            I capi vengono popolati automaticamente all'importazione del libro
            metodologia (vai al tab Libro).
          </p>
        </div>
      </section>
    )
  }

  return (
    <section className="tab-section">
      <h3>
        Capi <span className="muted">({items.length} totali)</span>
      </h3>

      <div className="filters">
        <input
          type="search"
          placeholder="Cerca per nome..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={filterSection} onChange={e => setFilterSection(e.target.value as GarmentSection | 'all')}>
          <option value="all">Tutte le sezioni</option>
          <option value="donna">Donna</option>
          <option value="uomo">Uomo</option>
          <option value="bambino">Bambino</option>
        </select>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
          <option value="all">Tutte le categorie</option>
          {categories.map(c => (
            <option key={c} value={c}>{CATEGORY_LABELS[c] ?? c}</option>
          ))}
        </select>
      </div>

      <div className="row" style={{ margin: '0.5rem 0 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="secondary" onClick={runDiagnostic} disabled={diagBusy || !book}>
          {diagBusy ? 'Diagnostica…' : diagnostic ? 'Ri-esegui diagnostica' : 'Diagnostica formule'}
        </button>
        {diagnostic && (
          <>
            <span className="diag-pill diag-ok">{diagnostic.ok} ok</span>
            <span className="diag-pill diag-warn">{diagnostic.warn} warn</span>
            <span className="diag-pill diag-error">{diagnostic.error} errore</span>
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value as typeof filterStatus)}
              style={{ width: 'auto', maxWidth: 220 }}
            >
              <option value="all">Mostra tutti</option>
              <option value="ok">Solo OK</option>
              <option value="warn">Solo warning</option>
              <option value="error">Solo errori</option>
            </select>
          </>
        )}
      </div>

      <p className="muted" style={{ margin: '0.6rem 0 0' }}>
        {visible.length} {visible.length === 1 ? 'capo' : 'capi'}
        {diagnostic && filterStatus !== 'all' ? ` (filtrati da ${sortedAlpha.length})` : ''}
      </p>

      <div className="garments-grid">
        {visible.map(item => {
          const c = item.content
          const diag = diagByName.get(c.name)
          return (
            <article
              key={item.id}
              className={`card clickable garment-card section-${c.section}`}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedId(item.id)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setSelectedId(item.id)
                }
              }}
            >
              <div className="garment-card-title">{c.name}</div>
              <div className="garment-card-badges">
                {isBaseGarment(c.name) && (
                  <span className="badge badge-base">BASE</span>
                )}
                <span className={`badge section-${c.section}`}>
                  {SECTION_LABELS[c.section]}
                </span>
                <span className="badge category">
                  {CATEGORY_LABELS[c.category] ?? c.category}
                </span>
                {c.scale && <span className="badge scale">scala {c.scale}</span>}
                {c.easeAllowanceDegree && (
                  <span className="badge ease">grado {c.easeAllowanceDegree}</span>
                )}
                {diag && (
                  <span
                    className={`diag-pill diag-${diag.status}`}
                    title={
                      diag.skipped ??
                      `${diag.pointsComputed}/${diag.pointsExpected} punti${diag.errorCount > 0 ? `, ${diag.errorCount} errori` : ''}`
                    }
                  >
                    {diag.status === 'ok'
                      ? '✓'
                      : diag.status === 'warn'
                        ? '⚠'
                        : '✗'}
                    {diag.errorCount > 0 ? ` ${diag.errorCount}` : ''}
                  </span>
                )}
              </div>
              <div className="garment-card-meta">
                pp. {c.startPage}{c.startPage !== c.endPage ? `-${c.endPage}` : ''}
              </div>
              <div className="garment-card-stats">
                <span><strong>{c.pieces.length}</strong> pezzi</span>
                <span><strong>{c.steps.length}</strong> passi</span>
                <span><strong>{c.measurementsRequired.length}</strong> misure</span>
              </div>
            </article>
          )
        })}
      </div>
    </section>
  )
}
